<script>
import findIndex from 'lodash/findIndex';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ReportModel from '@models/Report';
import CarUseModel from '@models/CarUse';
import FileDownloadLink from '@components/FileDownloadLink';
// import BlockingContainer from '@components/BlockingContainer';
import CarUseForm from '@views/reports/car-use-form';
import CarUseModal from '@views/reports/edit-car-use';
import utility from '@mixins/utility';

export default {
  components: {
    // BlockingContainer,
    FontAwesomeIcon,
    CarUseForm,
    CarUseModal,
    FileDownloadLink,
  },
  mixins: [utility],
  props: {
    collapsed: {
      default: false,
      type: Boolean,
      required: false,
    },
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    reportId: {
      required: true,
    },
    carUses: {
      default: () => [],
      type: Array,
    },
    locked: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      selectedCarUse: null,
      fields: [
        {
          key: 'car_type',
          label: this.$t('fields.car_type'),
          sortable: false,
        },
        {
          key: 'license_plate',
          label: this.$t('table_labels.license_plate'),
          sortable: true,
        },
        {
          key: 'owner',
          label: this.$t('fields.owner'),
          sortable: false,
        },
        {
          key: 'takeover_from',
          label: this.$t('fields.takeover_from'),
          sortable: true,
        },
        {
          key: 'return_to',
          label: this.$t('fields.return_to'),
          sortable: true,
        },
        {
          key: 'takeover_date',
          label: this.$t('fields.takeover_date'),
          sortable: true,
          formatter: (value) => (value ? DateTime.fromSQL(value).toFormat('dd.MM.yyyy HH:mm') : ''),
        },
        {
          key: 'return_date',
          label: this.$t('fields.return_date'),
          sortable: true,
          formatter: (value) => (value ? DateTime.fromSQL(value).toFormat('dd.MM.yyyy HH:mm') : ''),
        },
        {
          key: 'km_status_takeover',
          label: this.$t('fields.km_status_takeover'),
          sortable: true,
        },
        {
          key: 'km_status_return',
          label: this.$t('fields.km_status_return'),
          sortable: true,
        },
        {
          key: 'total_km',
          label: this.$t('fields.total_km'),
          sortable: false,
        },
        {
          key: 'completed',
          label: this.$t('fields.status'),
          sortable: false,
          class: 'text-left',
        },
        {
          key: 'paid',
          label: this.$t('fields.paid'),
          sortable: false,
          class: 'w-40 text-left',
        },
        {
          key: 'actions',
          label: this.$t('table_labels.actions'),
          sortable: false,
        },
      ],
    };
  },
  methods: {
    submit(carUse) {
      this.$emit('submitted', carUse);
    },
    update(carUse) {
      this.selectedCarUse = null;
      const key = findIndex(this.carUses, { id: carUse.id });
      this.$emit('updated', carUse, key);
      if (!carUse.completed) this.$store.dispatch('common/updateCurrentReportStatus', 0);
    },
    async deleteCarUse(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.car_use') }))) {
        this.$wait.start(`carUse${id}`);
        const key = findIndex(this.carUses, { id });
        const report = new ReportModel({ id: this.reportId });
        const carUse = new CarUseModel({ id });
        await carUse.for(report).delete();
        this.$emit('deleted', id, key);
        this.$wait.end(`carUse${id}`);
      }
    },
    dateDiff(item) {
      const date = DateTime.fromSQL(item.return_date).diff(DateTime.fromSQL(item.takeover_date), ['hours', 'minutes']).toObject();
      return `${date.hours}h ${date.minutes}m`;
    },
    checkboxChanged(id, item) {
      this.$wait.start(`statusLoader${id}`);
      const report = new ReportModel({ id: this.reportId });
      const carUse = new CarUseModel({
        id: `status/${id}`,
        paid: item.paid,
      });

      carUse
        .for(report)
        .save()
        .then(() => {
          this.$wait.end(`statusLoader${id}`);
        });
    },
  },
};
</script>

<template>
  <div>
    <b-collapse id="car-uses-collapse" v-model="collapsed" class="mt-2">
      <CarUseForm v-if="isReportEditable(locked)" ref="carUseForm" :loaded-id="loadedId" :report-id="reportId" @submitted="submit" />
    </b-collapse>
    <small v-if="carUses.length" class="table-responsive">
      <br />
      <b-table stacked="lg" striped hover :items="carUses" :fields="fields" small table-class="no-min-width">
        <template v-slot:cell(car_type)="row">
          <div v-if="row.item.car && row.item.car.car_type">
            {{ row.item.car.car_type.name }}
          </div>
          <div v-else> - </div>
        </template>
        <template v-slot:cell(license_plate)="row">
          <div v-if="row.item.car">
            {{ row.item.car.license_plate }}
          </div>
          <div v-else> - </div>
        </template>
        <template v-slot:cell(owner)="row">
          <div v-if="row.item.car && row.item.car.car_owner" class="no-wrap">{{ row.item.car.car_owner.name }}</div>
          <div v-else>-</div>
        </template>
        <template v-slot:cell(total_km)="row">
          <template v-if="row.item.km_status_return && row.item.km_status_takeover">
            {{ row.item.km_status_return - row.item.km_status_takeover }}
          </template>
          <template v-else> - </template>
        </template>
        <template v-slot:cell(completed)="row">
          <FontAwesomeIcon v-if="row.item.completed" class="text-success" :icon="['fas', 'check-circle']" />
          <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
        </template>
        <template v-slot:cell(paid)="row">
          <div v-can:superAdministrate :class="$style.statusContainer">
            <div v-show="!$wait.is(`statusLoader${row.item.id}`)">
              <b-form-checkbox v-model="row.item.paid" :value="1" :unchecked-value="0" @change="checkboxChanged(row.item.id, row.item)"> </b-form-checkbox>
            </div>

            <FontAwesomeIcon v-show="$wait.is(`statusLoader${row.item.id}`)" :class="$style.statuLoadingIcon" :icon="['fas', 'spinner']" pulse />
          </div>
          <div v-can:superAdministrate.not>
            <FontAwesomeIcon v-if="parseInt(row.item.paid, 10)" class="text-success" :icon="['fas', 'check-circle']" />
            <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
          </div>
        </template>
        <template v-slot:cell(actions)="row">
          <b-dropdown v-if="isReportEditable(locked)" variant="outline-primary" dropleft boundary="window" no-caret size="sm">
            <template #button-content>
              <FontAwesomeIcon :icon="['fas', 'ellipsis-h']" />
            </template>

            <b-dropdown-item variant="primary" @click="selectedCarUse = row.item.id">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
              <small>&nbsp;{{ $t('actions.edit') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="primary">
              <FileDownloadLink
                v-if="row.item.car && row.item.car.zub"
                :file="row.item.car.zub"
                :removable="false"
                btn-class="w-100 text-decoration-none"
                :small-text="true"
              >
                <FontAwesomeIcon :icon="['fas', 'file-export']" />
              </FileDownloadLink>
            </b-dropdown-item>

            <b-dropdown-item variant="danger" @click="deleteCarUse(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
              <small>&nbsp;{{ $t('actions.delete') }}</small>
            </b-dropdown-item>
          </b-dropdown>
          <div v-else>
            <FontAwesomeIcon :icon="['fas', 'lock']" />
          </div>
        </template>
      </b-table>
    </small>

    <CarUseModal v-model="selectedCarUse" :loaded-id="selectedCarUse" :report-id="reportId" @updated="update" @hidden="selectedCarUse = null" />
  </div>
</template>

<style lang="scss" module>
.statusContainer {
  margin: 0;
}

.statusLoadingIcon {
  margin: 0 auto;
}
</style>
